<script setup>
import { router, usePage } from '@inertiajs/vue3';
import { reactive, ref } from 'vue';
import JetButton from '@/Jetstream/Button.vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

// includes we need to front end locale magic
import { useI18n } from 'vue-i18n';

// Set up all the data variables we are going to initially need
const programme = router.page.props.ziggy.programme;
const organisation = router.page.props.ziggy.organisation;
const urlNamePrefix = organisation ? "gi." : "";
const user = router.page.props.auth.user;
const usePageProps = usePage().props;

// get locale as set in cookie
let { locale } = useI18n({ useScope: 'global' });

// get data
const props = defineProps({
    custom_field: Object,
    action_id: Number,
    loggedInUserId: Number,
    filters: Object,
    key: Number,
    checked: false
});

const validationErrors = reactive({
    emptyField: ''
});

const customField = props.custom_field;
const actionID = customField.action_id;
const fieldID =  'custom-field-' + customField.action_id + '-' + customField.id;

// Set up date variables
const date = ref();
const dateOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };

// Process field label
const fieldLabel = (jsonString, key) => {
    try {
    const jsonData = JSON.parse(jsonString); // Parse the JSON string
    const result = jsonData[key];
    return result; // Return the value associated with the key
    } catch (error) {
        console.error("Invalid JSON string", error);
        return undefined; // Return undefined if parsing fails
    }
}

// If the field is a select list, process the data.
function processSelectListData(input, key) {
    // Parse the input JSON string
    const data = JSON.parse(input);
    // Extract the comma-separated values from the "en" key
    const values = data[key].split(',');
    // Map the values to the desired format
    const result = values.map(value => ({
        name: value,
        value: value
    }));
    return result;
}
let selectOptions = [];
if (customField.type == 'select') {
    selectOptions = processSelectListData(props.custom_field.options, locale.value);
}

// If the field is a text field, process the data.
const processTextInputData = (jsonString, key) => {
    try {
    const jsonData = JSON.parse(jsonString); // Parse the JSON string
    const result = jsonData[key];
    return result; // Return the value associated with the key
    } catch (error) {
        console.error("Invalid JSON string", error);
        return undefined; // Return undefined if parsing fails
    }
}
let textDefaultValue = '';
if (customField.type == 'text') {
    textDefaultValue = processTextInputData(props.custom_field.default, locale.value);
}

// get answers
let customFieldAnswer = '';
// If answer already exists, get the value
//(there will only ever be one answer so we can get the first array value).
if ( customField.answer && Object.keys(customField.answer).length !== 0) {
    customFieldAnswer = Object.keys(customField.answer).map((k) => customField.answer[k]);
    customFieldAnswer = customFieldAnswer[0].answer;
}
else {
    customFieldAnswer = textDefaultValue;
}

// Assign the answer to a reactive variable
const customFieldForm = reactive({
    inputValue: customFieldAnswer,
});

// Toggle checkbox status
let isChecked = customFieldAnswer;
if (customField.type == 'checkbox') {
   isChecked == 1 ? (isChecked = true) : (isChecked = false);
}

// This is the function that posts updated field data to the database
const UpdateCustomField = ($event, custom_field) => {

// if this is a datepicker field, assign/format the chosen date from the reactive date variable
if (customField.type == 'date') {
    customFieldForm.inputValue = date.value.toLocaleDateString('en-uk', dateOptions);
}

// Only attempt to post to the database if the field has a value
if ( customFieldForm.inputValue != undefined || customFieldForm.inputValue != null || customFieldForm.inputValue != '' || customFieldForm.inputValue.length != 0 ) {

        router.post(route(urlNamePrefix+'action-custom-field.update'),
        {
            required: customField.required,
            custom_field_id: customField.id,
            current_team_id: user.current_team_id,
            action_id: customField.action_id,
            custom_field_answer: customFieldForm.inputValue,
            filters: props.filters
        }, {
            forceFormData: true,
            preserveState: true,
            preserveScroll: true,
            onSuccess: function() {
                validationErrors.emptyField = "";

            },
            onError: (errors) => {
                validationErrors.emptyField = "This field is required. You cannot save it if it is empty.";
            },

        });

    } else {
        validationErrors.emptyField = "You cannot save an empty field.";

    }
}


</script>

<template>
    <div class="justify-between w-full mb-5 form-control-group">
        <label class="block mb-2" :for="fieldID">
            {{ fieldLabel(customField.label, locale) }}
        </label>
        <div class="flex align-middle form-control-inner-wrapper">

            <div v-if="customField.type == 'text'" class="flex form-control grow">
                <input
                type="text"
                :name="fieldID"
                :id="fieldID"
                :value="customFieldForm.inputValue"
                @input="customFieldForm.inputValue = $event.target.value"
                class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>

            <div v-else-if="customField.type == 'textarea'" class="flex form-control grow">
                <textarea
                :name="fieldID"
                :id="fieldID"
                :value="customFieldForm.inputValue"
                @input="customFieldForm.inputValue = $event.target.value"
                class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                >{{customFieldForm.inputValue}}</textarea>
            </div>

            <div v-else-if="customField.type == 'number'" class="flex form-control grow">
                <input
                type="number"
                :name="fieldID"
                :id="fieldID"
                :value="customFieldForm.inputValue"
                @input="customFieldForm.inputValue = $event.target.value"
                class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>

            <div v-else-if="customField.type == 'select'" class="flex form-control grow">
                <select
                :name="fieldID"
                :id="fieldID"
                :value="customFieldForm.inputValue"
                @input="customFieldForm.inputValue = $event.target.value"
                class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                >
                    <option v-for="optionItem in selectOptions"
                        :name="optionItem.name"
                        :value="optionItem.value"
                    >{{optionItem.name}}</option>
                </select>
            </div>

            <div v-else-if="customField.type == 'checkbox'" class="flex form-control grow">
                <input
                type="checkbox"
                :checked="isChecked"
                :name="fieldID"
                :id="fieldID"
                :value="customFieldForm.inputValue"
                @change="customFieldForm.inputValue = $event.target.checked"
                class="mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>

            <div v-else-if="customField.type == 'range'" class="flex form-control grow">
                <input
                type="range"
                :name="fieldID"
                :id="fieldID"
                :min="customField.numberMin"
                :max="customField.numberMax"
                :step="customField.numberStep"
                :value="customFieldForm.inputValue"
                @input="customFieldForm.inputValue = $event.target.value"
                class="w-full mb-5 mr-5 border-gray-300 rounded-md shadow-sm action-custom-field grow focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                />
            </div>

            <div v-else-if="customField.type == 'date'" class="flex form-control grow">
                <div>
                    <Datepicker
                    v-model="date"
                    :ref="date"
                    :name="fieldID"
                    :id="fieldID"
                    @input="customFieldForm.inputValue = date"
                    />
                    <small>{{ $t('toolkit.custom_field.saved_date') }}: {{customFieldForm.inputValue}}</small>
                </div>
            </div>

            <div>
                <JetButton
                :id="fieldID + '-save'"
                type="button"
                class="justify-center block float-right rounded action-custom-field-button btn btn-action-control btn-toolkit-primary"
                v-on:click="UpdateCustomField($event, customField)"
                >
                {{ $t('toolkit.custom_field.save') }}
                </JetButton>
            </div>
        </div>
        <p class="mb-5 text-red-500 strong">{{validationErrors.emptyField}}</p>
    </div>
</template>
